<script setup lang="ts">
import { getColorValue } from '@global-types/colors';
import { GoogleMap, Marker, Circle } from 'vue3-google-map';
import { computed, ref, watch } from 'vue';
import type { GoogleMapLatLngLiteral, GoogleMap as GoogleMapType } from '@global-types/googleMap.ts';

type CircleOptions = Record<string, unknown>;

interface Props {
  latitude?: number | null,
  longitude?: number | null,
  zoom?: number,
  height?: string | number,
  isStatic?: boolean,
  hasFullscreenControl?: boolean,
  hasStreetViewControl?: boolean,
  hasZoomControl?: boolean,
  hasClickableIcons?: boolean,
  withCircle?: boolean,
  withMarker?: boolean,
  circleOptions?: CircleOptions | null,
}

const props = withDefaults(defineProps<Props>(), {
  latitude: null,
  longitude: null,
  zoom: 14,
  height: 100,
  isStatic: true,
  withCircle: false,
  withMarker: false,
  circleOptions: null,
  hasFullscreenControl: true,
  hasStreetViewControl: false,
  hasZoomControl: false,
  hasClickableIcons: false,
});

const emit = defineEmits<{(e: 'ready', value: GoogleMapType): void}>();

const apiKey: string = window.GOOGLE_API_KEY;

const center = computed<GoogleMapLatLngLiteral | undefined>(() => {
  if (props.latitude === null || props.longitude === null) {
    return undefined;
  }

  return {
    lat: props.latitude,
    lng: props.longitude,
  };
});

const circleOptions = computed<CircleOptions>(() => ({
  center: center.value,
  radius: 300,
  strokeColor: getColorValue('blue'),
  strokeOpacity: 1,
  strokeWeight: 2,
  fillColor: getColorValue('blue'),
  fillOpacity: 0.2,
  clickable: false,
  ...props.circleOptions,
}));

const style = {
  height: Number.isFinite(props.height)
    ? `${props.height}px`
    : props.height as string,
  width: 'auto',
};

const markerOptions = {
  position: center.value,
  icon: {
    url: '/images/ph_map-pin-espace.svg',
  },
};

const googleMap = ref<InstanceType<typeof GoogleMap> | null>(null);

watch(
  () => googleMap.value?.map,
  (map) => {
    if (!map) {
      return;
    }

    emit('ready', map);
  },
  { immediate: true },
);
</script>

<template>
  <GoogleMap
    ref="googleMap"
    :api-key="apiKey"
    :center="center"
    :zoom="zoom"
    :style="style"
    :zoom-control="!isStatic && hasZoomControl"
    :map-type-control="!isStatic"
    :scale-control="!isStatic"
    :street-view-control="!isStatic && hasStreetViewControl"
    :rotate-control="!isStatic"
    :fullscreen-control="!isStatic && hasFullscreenControl"
    :clickable-icons="!isStatic && hasClickableIcons"
    :disable-default-ui="true"
    :gesture-handling="!isStatic ? 'greedy' : 'none'"
  >
    <Circle
      v-if="withCircle"
      :options="circleOptions"
    />

    <Marker
      v-if="withMarker"
      :options="markerOptions"
    />

    <slot />
  </GoogleMap>
</template>
